import type {
  CardStandard,
  CardStandards,
  CardTitleValueHtml,
  LegacyLessonCardStack,
  LessonCard,
} from "@shared/models/card-stack"
import { parsePartialJSON } from "./partial-json-parser"

/** Append text to the chat response. */
export interface ChatAppendCommand {
  type: "chatAppend"
  textSuffix: string
  text: string
}

export interface SetTitleCommand {
  type: "setTitle"
  title: string
  originalTitle: string | null
}

/** Append text to a card's value. */
export interface CardAppendValueCommand {
  type: "cardAppendValue"
  id: string
  cardType: LessonCard["type"]
  valueSuffix: string
  value: string
  originalValue: string | null
  isDone: boolean
}

/** Set a card's standard IDs (not streamed). */
export interface CardSetStandardIdsCommand {
  type: "cardSetStandardIds"
  id: string
  standardIds: string[]
  originalStandards: CardStandard[]
}

/** Issue the AI's summary of its changes to a card, once it's done editing the card. */
export interface CardIssueChangesCommand {
  type: "cardIssueChanges"
  id: string
  // The card's title.
  title: string
  changes: string
}

export type AiStreamParserCommand =
  | ChatAppendCommand
  | SetTitleCommand
  | CardAppendValueCommand
  | CardSetStandardIdsCommand
  | CardIssueChangesCommand

type AiResponse = Array<
  | {
      type: "chat"
      text?: string
    }
  | {
      type: "changeTitle"
      title?: string
    }
  | {
      type: "changeCard"
      id?: string
      value?: { isDone: boolean; value: string }
      standardIds?: string[]
      changes?: string
    }
>

/**
 * Incrementally parses a streaming response from our lesson-editing AI assistant
 * (backend Spikey.Ai.Lesson).
 */
export class AiStreamParser {
  private accumulatedDelta = ""
  private previousParsedResponse: AiResponse = []
  private processedCommands = new Set<string>()

  /**
   * Accumulate chat text for the whole response in case the AI splits it
   * over multiple "chat" blocks.
   */
  private accumulatedChatText = ""

  constructor(
    /**
     * The lesson at the start of the editing session (not necessarily this message, if the user asked to revise).
     */
    private readonly originalLesson: LegacyLessonCardStack
  ) {}

  /**
   * Returns the original card with the given id.
   */
  findOriginalCard(id: string): LessonCard | undefined {
    return this.originalLesson.attributes.cards.find((card) => card.id === id)
  }

  /**
   * Apply a delta received from the assistant, returning the commands that can be
   * completed so far.
   */
  applyDelta(delta: string): AiStreamParserCommand[] {
    if (delta === "") return []

    this.accumulatedDelta += delta
    let currentParsedResponse: AiResponse
    try {
      currentParsedResponse = parsePartialJSON(
        this.accumulatedDelta,
        // Don't return some fields until we've parsed their value completely, to prevent
        // confusion below and conform to our "not streamed" guarantees.
        // Also mark card values with isDone.
        (keyName) => {
          if (keyName === "value") return "mark"
          else {
            return !["type", "id", "title", "standardIds", "cardType", "changes"].includes(keyName)
          }
        }
      ) as AiResponse
    } catch {
      // If parsing fails, return empty array and wait for more data.
      return []
    }

    // Process each item in the response array
    const commands: AiStreamParserCommand[] = []
    for (let i = 0; i < currentParsedResponse.length; i++) {
      const currentItem = currentParsedResponse[i]
      const previousItem = this.previousParsedResponse[i]

      if (!currentItem) continue

      if (currentItem.type === "chat") {
        const prevChatItem = previousItem?.type === "chat" ? previousItem : undefined
        commands.push(...this.processChatItem(currentItem, prevChatItem))
      } else if (currentItem.type === "changeTitle") {
        const prevChangeTitleItem = previousItem?.type === "changeTitle" ? previousItem : undefined
        commands.push(...this.processChangeTitleItem(currentItem, prevChangeTitleItem))
      } else if (currentItem.type === "changeCard") {
        if (!currentItem.id) continue
        const originalCard = this.findOriginalCard(currentItem.id)
        if (!originalCard) {
          console.error(`AI returned card with non-existent ID ${currentItem.id}, skipping`)
          continue
        }
        const prevChangeCardItem = previousItem?.type === "changeCard" ? previousItem : undefined
        commands.push(...this.processChangeCardItem(currentItem, prevChangeCardItem, originalCard))
      }
    }

    this.previousParsedResponse = currentParsedResponse
    return commands
  }

  private processChatItem(
    currentItem: { type: "chat"; text?: string },
    previousItem: { type: "chat"; text?: string } | undefined
  ): AiStreamParserCommand[] {
    if (currentItem.text === undefined) return []

    const currentText = currentItem.text || ""
    const previousText = previousItem?.text

    if (previousText === undefined || currentText !== previousText) {
      const textSuffix = currentText.slice(previousText?.length || 0)
      if (previousText === undefined && this.accumulatedChatText !== "") {
        // The AI split its response across multiple "chat" blocks.
        // Combine with Markdown newline.
        this.accumulatedChatText += "\n\n"
      }
      this.accumulatedChatText += textSuffix

      return [
        {
          type: "chatAppend",
          textSuffix,
          text: this.accumulatedChatText,
        },
      ]
    }

    return []
  }

  private processChangeTitleItem(
    currentItem: { type: "changeTitle"; title?: string },
    previousItem: { type: "changeTitle"; title?: string } | undefined
  ): AiStreamParserCommand[] {
    if (currentItem.title === undefined) return []

    if (previousItem?.title === undefined) {
      return [
        {
          type: "setTitle",
          title: currentItem.title,
          originalTitle: this.originalLesson.attributes.title ?? null,
        },
      ]
    }

    return []
  }

  private processChangeCardItem(
    currentItem: {
      type: "changeCard"
      id?: string
      value?: { isDone: boolean; value: string }
      standardIds?: string[]
      changes?: string
    },
    previousItem:
      | {
          type: "changeCard"
          id?: string
          value?: { isDone: boolean; value: string }
          standardIds?: string[]
          changes?: string
        }
      | undefined,
    originalCard: LessonCard
  ): AiStreamParserCommand[] {
    if (currentItem.id === undefined) return []

    const commands: AiStreamParserCommand[] = []

    if (currentItem.standardIds !== undefined) {
      const commandKey = `standards-${currentItem.id}`
      if (!this.processedCommands.has(commandKey)) {
        commands.push({
          type: "cardSetStandardIds",
          id: currentItem.id,
          standardIds: currentItem.standardIds,
          originalStandards: (originalCard as CardStandards).attributes.standards,
        })
        this.processedCommands.add(commandKey)
      }
    }

    // Check if value is being streamed
    if (currentItem.value !== undefined) {
      const currentValue = currentItem.value.value || ""
      const previousValue = previousItem?.value?.value

      if (previousValue === undefined || currentValue !== previousValue) {
        const valueSuffix = currentValue.slice(previousValue?.length || 0)

        commands.push({
          type: "cardAppendValue",
          id: currentItem.id,
          cardType: originalCard.type,
          valueSuffix,
          value: currentValue,
          originalValue: (originalCard as CardTitleValueHtml).attributes.value,
          isDone: currentItem.value.isDone,
        })
      }
    }

    if (currentItem.changes !== undefined) {
      const commandKey = `changes-${currentItem.id}`
      if (!this.processedCommands.has(commandKey)) {
        const title = originalCard.attributes["title"] || ""
        commands.push({
          type: "cardIssueChanges",
          id: currentItem.id,
          title,
          changes: currentItem.changes,
        })
        this.processedCommands.add(commandKey)
      }
    }

    return commands
  }
}
